import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import ErrorState from '../../components/ui/ErrorState';
import Toast from '../../components/ui/Toast';
import CatalogCardSkeleton from '../dashboard/components/CatalogCardSkeleton';
import { catalogService } from '../../services/catalogService';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

const STATUS_COLORS = {
  published: 'bg-success/90 text-white',
  draft: 'bg-warning/90 text-white',
  archived: 'bg-muted text-muted-foreground',
};

const LibraryCatalogCard = ({ catalog, onRestore, onView, onEdit }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden shadow-elevation-sm hover:shadow-elevation-md transition-all duration-250 group">
      <div className="relative aspect-[3/4] overflow-hidden bg-white">
        {catalog?.previewImages?.length > 0 ? (
          <div className="grid grid-cols-2 w-full h-full gap-px bg-border group-hover:scale-105 transition-transform duration-250">
            {catalog.previewImages.slice(0, 4).map((url, idx) => (
              <div key={idx} className="overflow-hidden bg-muted">
                <Image
                  src={url}
                  alt={`${catalog?.title} - ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {catalog.previewImages.length < 4 &&
              Array.from({ length: 4 - catalog.previewImages.length }).map((_, idx) => (
                <div key={`empty-${idx}`} className="bg-muted" />
              ))}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted group-hover:scale-105 transition-transform duration-250">
            <Icon name="BookOpen" size={48} className="text-muted-foreground" />
          </div>
        )}
        <div className={`absolute top-3 right-3 px-2 py-1 rounded-md text-xs font-medium ${STATUS_COLORS[catalog?.status] || STATUS_COLORS.draft}`}>
          {t(`library.statusBadge.${catalog?.status}`) || catalog?.status}
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2">
          {catalog?.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <Icon name="Calendar" size={14} />
          <span>{new Date(catalog?.createdAt)?.toLocaleDateString()}</span>
        </div>

        <div className="border-t border-border pt-3 flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              iconName="Eye"
              iconPosition="left"
              onClick={() => onView?.(catalog)}
            >
              {t('library.actions.view')}
            </Button>
            {catalog?.status !== 'archived' && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                iconName="Edit"
                iconPosition="left"
                onClick={() => onEdit?.(catalog)}
              >
                {t('library.actions.edit')}
              </Button>
            )}
          </div>
          {catalog?.status === 'archived' && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              iconName="PackageOpen"
              iconPosition="left"
              onClick={() => onRestore?.(catalog)}
            >
              {t('library.actions.restore')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const PDFLibrary = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, userProfile, loading: authLoading, activeCompanyId } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [catalogToRestore, setCatalogToRestore] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!authLoading && user && activeCompanyId) {
      fetchData();
    }
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
    };
  }, [authLoading, user, activeCompanyId]);

  const fetchData = async () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const catalogsData = await catalogService?.getCatalogs(activeCompanyId);
      if (controller?.signal?.aborted) return;

      const catalogIds = (catalogsData || []).map((c) => c.id);
      const previewImagesMap = await catalogService?.getCatalogPreviewImages(catalogIds).catch(() => ({}));
      if (controller?.signal?.aborted) return;

      const catalogsWithPreviews = (catalogsData || []).map((c) => ({
        ...c,
        previewImages: previewImagesMap[c.id] || [],
      }));
      setCatalogs(catalogsWithPreviews);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching library data:', err);
      setError(err?.message || t('library.error.title'));
    } finally {
      if (!abortControllerRef?.current?.signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const filteredCatalogs = useMemo(() => {
    let filtered = [...catalogs];

    if (statusFilter !== 'all') {
      filtered = filtered?.filter((cat) => cat?.status === statusFilter);
    }

    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter((cat) => cat?.title?.toLowerCase()?.includes(query));
    }

    if (dateRange !== 'all') {
      const now = new Date();
      filtered = filtered?.filter((cat) => {
        const catDate = new Date(cat.createdAt);
        switch (dateRange) {
          case 'today':
            return catDate?.toDateString() === now?.toDateString();
          case 'week':
            return catDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          case 'month':
            return catDate?.getMonth() === now?.getMonth() && catDate?.getFullYear() === now?.getFullYear();
          case 'quarter': {
            const quarter = Math.floor(now?.getMonth() / 3);
            const catQuarter = Math.floor(catDate?.getMonth() / 3);
            return catQuarter === quarter && catDate?.getFullYear() === now?.getFullYear();
          }
          case 'year':
            return catDate?.getFullYear() === now?.getFullYear();
          default:
            return true;
        }
      });
    }

    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.title?.localeCompare(b?.title);
        case 'status': {
          const order = { published: 0, draft: 1, archived: 2 };
          return (order[a?.status] ?? 3) - (order[b?.status] ?? 3);
        }
        case 'date':
        default:
          return new Date(b?.createdAt) - new Date(a?.createdAt);
      }
    });

    return filtered;
  }, [catalogs, searchQuery, statusFilter, dateRange, sortBy]);

  const hasActiveFilters = searchQuery?.trim() || statusFilter !== 'all' || dateRange !== 'all';

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateRange('all');
  };

  const handleRestoreClick = (catalog) => {
    setCatalogToRestore(catalog);
    setShowRestoreModal(true);
  };

  const handleRestoreConfirm = async () => {
    if (!catalogToRestore) return;
    try {
      setRestoringId(catalogToRestore?.id);
      await catalogService?.restoreCatalog(catalogToRestore?.id);
      setCatalogs(prev =>
        prev?.map(c => c?.id === catalogToRestore?.id ? { ...c, status: 'draft' } : c)
      );
      setToasts(prev => [...prev, { id: Date.now(), type: 'success', message: t('library.toasts.restoreSuccess') }]);
    } catch (err) {
      console.error('Error restoring catalog:', err);
      setToasts(prev => [...prev, { id: Date.now(), type: 'error', message: t('library.toasts.restoreError') }]);
    } finally {
      setRestoringId(null);
      setShowRestoreModal(false);
      setCatalogToRestore(null);
    }
  };

  const handleView = (catalog) => {
    navigate(`/catalog-viewer?id=${catalog?.id}`);
  };

  const handleEdit = (catalog) => {
    navigate(`/catalog-editor?id=${catalog?.id}`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <div className={cn('transition-all duration-250', sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]')}>
          <div className="p-4 md:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <div className="h-10 w-56 bg-muted animate-pulse rounded-md mb-2" />
                <div className="h-5 w-80 bg-muted animate-pulse rounded-md" />
              </div>
              <div className="h-12 w-40 bg-muted animate-pulse rounded-md" />
            </div>
            <div className="bg-card rounded-lg border border-border p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-10 bg-muted animate-pulse rounded-md" />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <CatalogCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <div className={cn('transition-all duration-250', sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]')}>
          <div className="flex items-center justify-center min-h-screen">
            <ErrorState
              title={t('library.error.title')}
              message={error}
              onRetry={fetchData}
              retryText={t('library.error.retry')}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div className={cn('transition-all duration-250', sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]')}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
                {t('library.title')}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {t('library.subtitle')}
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate('/pdf-upload')}
              iconName="Plus"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {t('library.newCatalog')}
            </Button>
          </div>

          {/* Filters */}
          <div className="bg-card rounded-lg border border-border p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Search */}
              <div className="md:col-span-2 relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('library.filters.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">{t('library.filters.status.all')}</option>
                <option value="draft">{t('library.filters.status.draft')}</option>
                <option value="published">{t('library.filters.status.published')}</option>
                <option value="archived">{t('library.filters.status.archived')}</option>
              </select>

              {/* Date range */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e?.target?.value)}
                className="px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">{t('library.filters.dateRange.all')}</option>
                <option value="today">{t('library.filters.dateRange.today')}</option>
                <option value="week">{t('library.filters.dateRange.week')}</option>
                <option value="month">{t('library.filters.dateRange.month')}</option>
                <option value="quarter">{t('library.filters.dateRange.quarter')}</option>
                <option value="year">{t('library.filters.dateRange.year')}</option>
              </select>
            </div>

            <div className="flex items-center justify-between gap-3 mt-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('library.filters.sortBy.label')}:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="px-3 py-1.5 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="date">{t('library.filters.sortBy.date')}</option>
                  <option value="name">{t('library.filters.sortBy.name')}</option>
                  <option value="status">{t('library.filters.sortBy.status')}</option>
                </select>
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters} iconName="X" iconPosition="left">
                  {t('library.filters.clearFilters')}
                </Button>
              )}
            </div>
          </div>

          {/* Catalog grid or empty state */}
          {filteredCatalogs?.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center shadow-elevation-sm">
              <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                <Icon name={hasActiveFilters ? 'Search' : 'BookOpen'} size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {hasActiveFilters ? t('library.empty.noResults') : t('library.empty.title')}
              </h3>
              {!hasActiveFilters && (
                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                  {t('library.empty.description')}
                </p>
              )}
              {hasActiveFilters ? (
                <Button variant="outline" onClick={handleClearFilters} iconName="X" iconPosition="left">
                  {t('library.empty.clearFilters')}
                </Button>
              ) : (
                <Button variant="default" onClick={() => navigate('/pdf-upload')} iconName="Plus" iconPosition="left">
                  {t('library.empty.createButton')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {filteredCatalogs?.map((catalog) => (
                  <LibraryCatalogCard
                    key={catalog?.id}
                    catalog={catalog}
                    onRestore={handleRestoreClick}
                    onView={handleView}
                    onEdit={handleEdit}
                  />
                ))}
              </div>
              <div className="mt-6 text-sm text-muted-foreground">
                {filteredCatalogs?.length} {filteredCatalogs?.length === 1 ? 'catálogo' : 'catálogos'}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Restore Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRestoreModal}
        onClose={() => { setShowRestoreModal(false); setCatalogToRestore(null); }}
        onConfirm={handleRestoreConfirm}
        title={t('library.modals.restore.title')}
        message={t('library.modals.restore.description', { title: catalogToRestore?.title })}
        confirmText={t('library.modals.restore.confirmButton')}
        cancelText={t('library.modals.restore.cancelButton')}
        variant="warning"
        icon="PackageOpen"
        isLoading={!!restoringId}
      />

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts?.map((toast) => (
          <Toast
            key={toast?.id}
            message={toast?.message}
            type={toast?.type}
            onClose={() => setToasts(prev => prev?.filter(t => t?.id !== toast?.id))}
          />
        ))}
      </div>
    </div>
  );
};

export default PDFLibrary;
